import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { SELECTORS } from '@oneaudi/stck-store';
import {
  useServicesContext,
  VehicleBasic,
  useMandatoryAreaSearchContext,
  MandatoryAreaSearchState,
} from '@oneaudi/vtp-shared';
import Tiles from './Tiles';
import Tile from './Tile';
import { ContentService } from '@oneaudi/content-service';
import ResultsBar from './ResultsBar';
import { LayoutContainer } from './Tiles.styles';
import LoadMoreButton from './LoadMoreButton';
import { TileContext, TileContextState } from '../../hooks/tileHook';
import ZeroResultsPage from './ZeroResultsPage';
import { LocationRequired } from './LocationRequired';

interface Props {}

const Results: React.FC<Props> = () => {
  const resultVehicles: VehicleBasic[] = Array.from(
    useSelector((state) => SELECTORS.VEHICLES.getResultVehiclesMap(state as {})).values()
  );

  const audiContentService = useServicesContext().getAdditionalService(
    'audi-content-service'
  ) as ContentService;
  const content = audiContentService.getContent();
  const { mandatoryAreaSearchState, isMandatoryAreaSearchModalOpen, openMandatoryAreaSearchModal } =
    useMandatoryAreaSearchContext();
  const tileContextValue: TileContextState = useMemo(() => {
    return {
      appContext: 'results',
      contentConfiguration: content.fields,
    };
  }, []);

  const [focusIndex, setFocusIndex] = useState<number | null>(null);
  const prevVehicleCountRef = useRef(resultVehicles.length);

  useEffect(() => {
    if (focusIndex === null && resultVehicles.length > prevVehicleCountRef.current) {
      setFocusIndex(prevVehicleCountRef.current);
    }
    prevVehicleCountRef.current = resultVehicles.length;
  }, [resultVehicles?.length]);

  const appContext = content?.fields?.appContext || 'results';
  const shouldBlockResults = mandatoryAreaSearchState === MandatoryAreaSearchState.ENABLED;
  let resultsContent;

  if (shouldBlockResults) {
    resultsContent = (
      <LocationRequired
        onAction={openMandatoryAreaSearchModal}
        showNotification={!isMandatoryAreaSearchModalOpen}
      />
    );
  } else if (resultVehicles.length === 0) {
    resultsContent = <ZeroResultsPage context="results" />;
  } else {
    resultsContent = (
      <>
        <Tiles>
          {resultVehicles.map((resultVehicle, index) => (
            <Tile
              key={resultVehicle.id}
              vehicle={resultVehicle}
              focusOnMount={index === focusIndex}
            />
          ))}
        </Tiles>
        <LoadMoreButton onLoadMore={() => setFocusIndex(null)} />
      </>
    );
  }

  return (
    <TileContext.Provider value={tileContextValue}>
      <LayoutContainer direction="column" appContext={appContext}>
        <ResultsBar />
        {resultsContent}
      </LayoutContainer>
    </TileContext.Provider>
  );
};

export default Results;
